import * as THREE from 'three';

import { SceneManager } from './SceneManager';

/**
 * rAF-based animation loop — capped at 60 FPS to reduce GPU load on high-refresh displays.
 * Matches original app.js timing model: per-frame increments, no dt normalization for rotations.
 * dt is passed to systems that need wall-clock time (planets, portal, transitions).
 *
 * Render pipeline:
 *   Pass 1: bloom (composer.render) — full scene (layer 0)
 *   Pass 2: depth restore — colorMask off, clearDepth, star depthCoreOnly=1, render, restore
 *   Pass 3: logo (postRender) — depth-tested against star cores
 *   Pass 4: portal — rendered by main renderer with toneMapping off, depth-tested against scene
 */
const TARGET_FPS = 60;
const FRAME_INTERVAL = 1000 / TARGET_FPS;

export class AnimationLoop {
  private frameId = 0;
  private lastFrameTime = 0;
  private lastRenderTime = 0;
  private elapsed = 0;
  private running = false;

  private tickSubscribers: ((dt: number) => void)[] = [];
  private postRenderSubscribers: (() => void)[] = [];

  // Star material reference (set after stars are created)
  private starMaterial: THREE.ShaderMaterial | null = null;

  constructor(private sceneManager: SceneManager) {}

  /** Set star material for depth-restore pass (depthCoreOnly uniform) */
  setStarMaterial(material: THREE.ShaderMaterial): void {
    this.starMaterial = material;
  }

  /** Subscribe to tick. Returns unsubscribe function. */
  onTick(callback: (dt: number) => void): () => void {
    this.tickSubscribers.push(callback);
    return () => {
      this.tickSubscribers = this.tickSubscribers.filter((cb) => cb !== callback);
    };
  }

  /** Subscribe to post-render (after bloom). Returns unsubscribe function. */
  onPostRender(callback: () => void): () => void {
    this.postRenderSubscribers.push(callback);
    return () => {
      this.postRenderSubscribers = this.postRenderSubscribers.filter((cb) => cb !== callback);
    };
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastFrameTime = performance.now();
    this.lastRenderTime = 0;
    this.loop();
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frameId);
  }

  private loop = (): void => {
    if (!this.running) return;
    this.frameId = requestAnimationFrame(this.loop);

    const now = performance.now();
    let dt = (now - this.lastFrameTime) / 1000; // seconds
    this.lastFrameTime = now;
    // Clamp huge jumps (tab switch, first frame) — matches original app.js line 4731
    dt = Math.min(Math.max(dt, 0), 0.05);
    this.elapsed += dt;

    // Always tick systems at native refresh rate (keeps animations smooth)
    for (let i = 0; i < this.tickSubscribers.length; i++) {
      this.tickSubscribers[i](dt);
    }

    // Update controls
    this.sceneManager.controls.update();

    // Skip GPU render if not enough time has passed (60 FPS render cap)
    if (now - this.lastRenderTime < FRAME_INTERVAL) return;
    this.lastRenderTime = now;

    const { camera, renderer, scene, composer, portalScene } = this.sceneManager;
    const prevAutoClear = renderer.autoClear;

    // Pass 1: bloom — layer 0
    camera.layers.set(0);
    composer.render();

    // Pass 2: depth restore (matches original app.js colorMask trick)
    // Bloom's fullscreen quad overwrites screen depth. Re-render scene with
    // colorMask off so only depth is written. Star depthCoreOnly=1 discards
    // glow/halo fragments so only star cores occlude the logo.
    // Always restore depth — needed for both logo and portal occlusion.
    renderer.autoClear = false;
    const gl = renderer.getContext();
    gl.colorMask(false, false, false, false);
    renderer.clearDepth();
    if (this.starMaterial) {
      this.starMaterial.depthWrite = true;
      this.starMaterial.depthTest = true;
      this.starMaterial.uniforms.depthCoreOnly.value = 1.0;
    }
    renderer.render(scene, camera);
    if (this.starMaterial) {
      this.starMaterial.depthWrite = false;
      this.starMaterial.uniforms.depthCoreOnly.value = 0.0;
    }
    gl.colorMask(true, true, true, true);

    // Post-render (logo — depth-tested against star cores, no bloom)
    for (let i = 0; i < this.postRenderSubscribers.length; i++) {
      this.postRenderSubscribers[i]();
    }

    // Pass 4: portal — rendered into main canvas with depth testing
    // Portal materials use depthTest:true so they are correctly occluded by the ship.
    // toneMapping disabled to preserve original portal colors.
    const prevTone = renderer.toneMapping;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.render(portalScene, camera);
    renderer.toneMapping = prevTone;

    renderer.autoClear = prevAutoClear;

    // Restore all layers for raycasting
    camera.layers.enableAll();
  };
}
